// BL-177 Step 2l: one place that holds "who has what" for the Communications screen and the app-wide pop-ups.
// It reads three things the database lets this person see (their own participant rows, the colleague cards, the requests
// that involve them) and refreshes on a live change, on a short timer, and when asked.
import { useCallback, useEffect, useRef, useState } from 'react';
import { loadConversationAccess } from '../../lib/conversationAccess';

const EMPTY = { mineIds: new Set(), mineRows: [], cards: [], requests: [], available: true, error: '', version: 0 };
export const ACCESS_CHANGED_EVENT = 'ia:conversation-access-changed';

export default function useConversationAccess({ client, userId, pollMs = 30000 }) {
    const [state, setState] = useState(EMPTY);
    const [loaded, setLoaded] = useState(false);
    const aliveRef = useRef(true);
    const timerRef = useRef(null);

    const reload = useCallback(async () => {
        if (!client || !userId) return EMPTY;
        const next = await loadConversationAccess(client, userId);
        if (aliveRef.current) { setState((current) => ({ ...next, version: current.version + 1 })); setLoaded(true); }
        return next;
    }, [client, userId]);

    useEffect(() => {
        aliveRef.current = true;
        reload();
        return () => { aliveRef.current = false; };
    }, [reload]);

    useEffect(() => {
        if (!pollMs || !userId) return undefined;
        const interval = setInterval(() => {
            if (typeof document === 'undefined' || document.visibilityState !== 'hidden') reload();
        }, pollMs);
        return () => clearInterval(interval);
    }, [pollMs, reload, userId]);

    // Live: any change to a participant row or a request I am allowed to see (row-level security decides which).
    useEffect(() => {
        if (!client || !userId || typeof client.channel !== 'function') return undefined;
        let channel = null;
        const nudge = () => {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => reload(), 250);
        };
        try {
            channel = client.channel(`ia-conv-access-${userId}`)
                .on('postgres_changes', { event: '*', schema: 'public', table: 'inbox_conversation_members' }, nudge)
                .on('postgres_changes', { event: '*', schema: 'public', table: 'conversation_access_requests' }, nudge)
                .subscribe();
        } catch { /* the timer is the backup */ }
        return () => {
            clearTimeout(timerRef.current);
            try { if (channel) client.removeChannel(channel); } catch { /* already closed */ }
        };
    }, [client, reload, userId]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handler = () => reload();
        window.addEventListener(ACCESS_CHANGED_EVENT, handler);
        return () => window.removeEventListener(ACCESS_CHANGED_EVENT, handler);
    }, [reload]);

    return { ...state, loaded, reload };
}

/** Tell every screen that holds conversation access to refresh (after Allow / Decline / Take / Leave). */
export function announceAccessChanged() {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ACCESS_CHANGED_EVENT));
}
